import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import { toast } from 'sonner';
import {
  Search,
  Filter,
  Plus,
  MoreVertical,
  MessageSquare,
  Calendar,
  DollarSign,
  ChevronRight,
  X,
  FileText,
  Clock,
  User,
  MapPin,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const RequestsPage = () => {
  const { api } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteItems, setQuoteItems] = useState([{ description: '', quantity: 1, unit_price: 0, discount_percent: 0 }]);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteDiscount, setQuoteDiscount] = useState(0);
  const [quoteGST, setQuoteGST] = useState(18);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [requestsRes, categoriesRes, statusesRes, usersRes] = await Promise.all([
        api.get('/requests'),
        api.get('/service-categories'),
        api.get('/request-statuses'),
        api.get('/users')
      ]);
      setRequests(requestsRes.data);
      setCategories(categoriesRes.data);
      setStatuses(statusesRes.data);
      setTeamMembers(usersRes.data.filter(u => u.role !== 'client'));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      await api.patch(`/requests/${requestId}`, { status: newStatus });
      setRequests(requests.map(req => 
        req.id === requestId ? { ...req, status: newStatus } : req
      ));
      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, status: newStatus });
      }
      toast.success('Status updated');
    } catch (error) {
      console.error('Failed to update request:', error);
      toast.error('Failed to update status');
    }
  };

  const assignRequest = async (requestId, assigneeId) => {
    try {
      await api.patch(`/requests/${requestId}`, { assigned_to: assigneeId });
      fetchData();
      toast.success('Request assigned');
    } catch (error) {
      console.error('Failed to assign request:', error);
      toast.error('Failed to assign request');
    }
  };

  const addComment = async () => {
    if (!newComment.trim() || !selectedRequest) return;
    try {
      await api.post(`/requests/${selectedRequest.id}/comments`, {
        text: newComment,
        is_internal: isInternalComment
      });
      // Refresh request details
      const response = await api.get(`/requests/${selectedRequest.id}`);
      setSelectedRequest(response.data);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const createQuote = async () => {
    if (!selectedRequest) return;
    try {
      const quoteData = {
        request_id: selectedRequest.id,
        line_items: quoteItems.filter(item => item.description && item.unit_price > 0),
        notes: quoteNotes,
        discount_percent: quoteDiscount,
        gst_percent: quoteGST
      };
      const response = await api.post('/quotes', quoteData);
      toast.success('Quote created');
      setQuoteDialogOpen(false);
      // Send quote immediately
      await api.patch(`/quotes/${response.data.id}/send`);
      toast.success('Quote sent to client');
      fetchData();
    } catch (error) {
      console.error('Failed to create quote:', error);
      toast.error('Failed to create quote');
    }
  };

  const getStatusColor = (statusId) => {
    const status = statuses.find(s => s.id === statusId);
    const colors = {
      blue: 'bg-blue-500',
      yellow: 'bg-yellow-500',
      purple: 'bg-purple-500',
      green: 'bg-green-500',
      cyan: 'bg-cyan-500',
      orange: 'bg-orange-500',
      teal: 'bg-teal-500',
      gray: 'bg-gray-500',
      red: 'bg-red-500'
    };
    return colors[status?.color] || 'bg-gray-500';
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  const filteredRequests = requests.filter(req => {
    const matchesSearch = req.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      req.client?.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      req.request_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || req.service_category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculateQuoteTotal = () => {
    const subtotal = quoteItems.reduce((sum, item) => {
      const itemTotal = item.quantity * item.unit_price;
      const itemDiscount = itemTotal * (item.discount_percent / 100);
      return sum + (itemTotal - itemDiscount);
    }, 0);
    const discountAmount = subtotal * (quoteDiscount / 100);
    const taxableAmount = subtotal - discountAmount;
    const gstAmount = taxableAmount * (quoteGST / 100);
    return {
      subtotal,
      discountAmount,
      taxableAmount,
      gstAmount,
      grandTotal: taxableAmount + gstAmount
    };
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="requests-page-title">Service Requests</h1>
            <p className="text-fp-textMuted text-sm mt-1">{requests.length} total requests</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fp-textMuted" />
              <Input
                placeholder="Search requests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-fp-surface border-white/10 text-white w-64"
                data-testid="requests-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-fp-surface border-white/10 text-white" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent className="bg-fp-surface border-white/10">
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map(status => (
                  <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40 bg-fp-surface border-white/10 text-white" data-testid="category-filter">
                <SelectValue placeholder="All Services" />
              </SelectTrigger>
              <SelectContent className="bg-fp-surface border-white/10">
                <SelectItem value="all">All Services</SelectItem>
                {categories.filter(c => c.active).map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Requests Table */}
        <div className="bg-fp-surface rounded-xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-6 py-4 text-fp-textMuted text-sm font-medium">Request</th>
                  <th className="text-left px-6 py-4 text-fp-textMuted text-sm font-medium">Client</th>
                  <th className="text-left px-6 py-4 text-fp-textMuted text-sm font-medium">Service</th>
                  <th className="text-left px-6 py-4 text-fp-textMuted text-sm font-medium">Status</th>
                  <th className="text-left px-6 py-4 text-fp-textMuted text-sm font-medium">Assigned To</th>
                  <th className="text-left px-6 py-4 text-fp-textMuted text-sm font-medium">Date</th>
                  <th className="text-left px-6 py-4 text-fp-textMuted text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-fp-textMuted">
                      Loading...
                    </td>
                  </tr>
                ) : filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-fp-textMuted">
                      No requests found
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((request) => (
                    <tr 
                      key={request.id} 
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                      onClick={() => {
                        setSelectedRequest(request);
                        setDetailOpen(true);
                      }}
                      data-testid={`request-row-${request.id}`}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white font-medium">{request.title}</p>
                          <p className="text-fp-textMuted text-sm">{request.request_number}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-white">{request.client?.company_name || 'N/A'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-primary/10 text-primary text-sm rounded">
                          {getCategoryName(request.service_category)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${getStatusColor(request.status)}`} />
                          <span className="text-white capitalize">{request.status?.replace('_', ' ')}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-white">{request.assignee?.name || 'Unassigned'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-fp-textMuted text-sm">{formatDate(request.created_at)}</p>
                      </td>
                      <td className="px-6 py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <button className="p-2 hover:bg-white/10 rounded">
                              <MoreVertical className="w-4 h-4 text-fp-textMuted" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-fp-surface border-white/10">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRequest(request);
                              setQuoteDialogOpen(true);
                            }}>
                              <DollarSign className="w-4 h-4 mr-2" />
                              Create Quote
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRequest(request);
                              setDetailOpen(true);
                            }}>
                              <FileText className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Request Detail Modal */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="bg-fp-surface border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Request Details</DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-fp-textMuted text-sm">{selectedRequest.request_number}</p>
                    <h2 className="text-xl font-semibold text-white mt-1">{selectedRequest.title}</h2>
                    <p className="text-fp-textMuted mt-1">
                      {selectedRequest.client?.company_name || 'Unknown Client'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(selectedRequest.status)}`} />
                    <span className="text-white capitalize">{selectedRequest.status?.replace('_', ' ')}</span>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-3 flex-wrap">
                  <Button
                    onClick={() => setQuoteDialogOpen(true)}
                    className="btn-primary"
                    data-testid="create-quote-btn"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Create Quote
                  </Button>
                  <Select 
                    value={selectedRequest.assigned_to || ''} 
                    onValueChange={(value) => assignRequest(selectedRequest.id, value)}
                  >
                    <SelectTrigger className="w-48 bg-fp-bg border-white/10 text-white">
                      <SelectValue placeholder="Assign to..." />
                    </SelectTrigger>
                    <SelectContent className="bg-fp-surface border-white/10">
                      {teamMembers.map(member => (
                        <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-fp-bg rounded-lg">
                    <label className="text-fp-textMuted text-xs flex items-center gap-1">
                      <FileText className="w-3 h-3" /> Service
                    </label>
                    <p className="text-white mt-1">{getCategoryName(selectedRequest.service_category)}</p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-lg">
                    <label className="text-fp-textMuted text-xs flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> Location
                    </label>
                    <p className="text-white mt-1">{selectedRequest.location || 'Not specified'}</p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-lg">
                    <label className="text-fp-textMuted text-xs flex items-center gap-1">
                      <DollarSign className="w-3 h-3" /> Budget
                    </label>
                    <p className="text-white mt-1">{selectedRequest.budget_range || 'Not specified'}</p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-lg">
                    <label className="text-fp-textMuted text-xs flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Preferred Dates
                    </label>
                    <p className="text-white mt-1">
                      {selectedRequest.preferred_dates?.length > 0 
                        ? selectedRequest.preferred_dates.join(', ') 
                        : 'Flexible'}
                    </p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-lg">
                    <label className="text-fp-textMuted text-xs flex items-center gap-1">
                      <User className="w-3 h-3" /> Assigned To
                    </label>
                    <p className="text-white mt-1">{selectedRequest.assignee?.name || 'Unassigned'}</p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-lg">
                    <label className="text-fp-textMuted text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Created
                    </label>
                    <p className="text-white mt-1">{formatDate(selectedRequest.created_at)}</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-fp-textMuted text-xs block mb-2">Description</label>
                  <p className="p-4 bg-fp-bg rounded-lg text-white text-sm whitespace-pre-wrap">
                    {selectedRequest.description}
                  </p>
                </div>

                {/* Status Change */}
                <div>
                  <label className="text-fp-textMuted text-xs block mb-2">Update Status</label>
                  <div className="flex flex-wrap gap-2">
                    {statuses.map((status) => (
                      <button
                        key={status.id}
                        onClick={() => updateRequestStatus(selectedRequest.id, status.id)}
                        className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                          selectedRequest.status === status.id
                            ? 'bg-primary text-black'
                            : 'bg-white/5 text-fp-textMuted hover:bg-white/10'
                        }`}
                        data-testid={`status-btn-${status.id}`}
                      >
                        {status.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quote Info */}
                {selectedRequest.quote && (
                  <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-400 font-medium">Quote #{selectedRequest.quote.quote_number}</p>
                        <p className="text-fp-textMuted text-sm">Status: {selectedRequest.quote.status}</p>
                      </div>
                      <p className="text-2xl font-bold text-green-400">
                        {formatCurrency(selectedRequest.quote.grand_total)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Comments Section */}
                <div>
                  <label className="text-fp-textMuted text-xs block mb-3">Comments & Activity</label>
                  <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                    {selectedRequest.comments?.map((comment) => (
                      <div 
                        key={comment.id} 
                        className={`p-3 rounded-lg ${comment.is_internal ? 'bg-yellow-500/10 border border-yellow-500/20' : 'bg-fp-bg'}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white text-sm font-medium">{comment.author_name}</span>
                          <span className="text-fp-textMuted text-xs">{formatDate(comment.created_at)}</span>
                        </div>
                        <p className="text-fp-textMuted text-sm">{comment.text}</p>
                        {comment.is_internal && (
                          <span className="text-yellow-400 text-xs mt-1 inline-block">Internal Note</span>
                        )}
                      </div>
                    ))}
                    {(!selectedRequest.comments || selectedRequest.comments.length === 0) && (
                      <p className="text-fp-textMuted text-sm text-center py-4">No comments yet</p>
                    )}
                  </div>
                  
                  {/* Add Comment */}
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        className="bg-fp-bg border-white/10 text-white resize-none"
                        rows={2}
                        data-testid="comment-input"
                      />
                      <label className="flex items-center gap-2 mt-2 text-fp-textMuted text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isInternalComment}
                          onChange={(e) => setIsInternalComment(e.target.checked)}
                          className="rounded border-white/10"
                        />
                        Internal note (not visible to client)
                      </label>
                    </div>
                    <Button onClick={addComment} className="btn-primary self-start" data-testid="add-comment-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Quote Creation Dialog */}
        <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
          <DialogContent className="bg-fp-surface border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Create Quote</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Line Items */}
              <div>
                <label className="text-fp-textMuted text-xs block mb-3">Line Items</label>
                {quoteItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 mb-2">
                    <div className="col-span-5">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => {
                          const newItems = [...quoteItems];
                          newItems[index].description = e.target.value;
                          setQuoteItems(newItems);
                        }}
                        className="bg-fp-bg border-white/10 text-white"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => {
                          const newItems = [...quoteItems];
                          newItems[index].quantity = parseInt(e.target.value) || 1;
                          setQuoteItems(newItems);
                        }}
                        className="bg-fp-bg border-white/10 text-white"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => {
                          const newItems = [...quoteItems];
                          newItems[index].unit_price = parseFloat(e.target.value) || 0;
                          setQuoteItems(newItems);
                        }}
                        className="bg-fp-bg border-white/10 text-white"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Disc %"
                        value={item.discount_percent}
                        onChange={(e) => {
                          const newItems = [...quoteItems];
                          newItems[index].discount_percent = parseFloat(e.target.value) || 0;
                          setQuoteItems(newItems);
                        }}
                        className="bg-fp-bg border-white/10 text-white"
                      />
                    </div>
                    <div className="col-span-1">
                      <button
                        onClick={() => setQuoteItems(quoteItems.filter((_, i) => i !== index))}
                        className="p-2 hover:bg-red-500/20 text-red-400 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                <Button
                  onClick={() => setQuoteItems([...quoteItems, { description: '', quantity: 1, unit_price: 0, discount_percent: 0 }])}
                  variant="outline"
                  className="mt-2 border-white/10 text-fp-textMuted hover:text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </div>

              {/* Discount & GST */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-fp-textMuted text-xs block mb-2">Overall Discount %</label>
                  <Input
                    type="number"
                    value={quoteDiscount}
                    onChange={(e) => setQuoteDiscount(parseFloat(e.target.value) || 0)}
                    className="bg-fp-bg border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-fp-textMuted text-xs block mb-2">GST %</label>
                  <Input
                    type="number"
                    value={quoteGST}
                    onChange={(e) => setQuoteGST(parseFloat(e.target.value) || 18)}
                    className="bg-fp-bg border-white/10 text-white"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-fp-textMuted text-xs block mb-2">Notes</label>
                <Textarea
                  value={quoteNotes}
                  onChange={(e) => setQuoteNotes(e.target.value)}
                  placeholder="Additional notes for the client..."
                  className="bg-fp-bg border-white/10 text-white resize-none"
                  rows={3}
                />
              </div>

              {/* Totals */}
              <div className="p-4 bg-fp-bg rounded-lg space-y-2">
                <div className="flex justify-between text-fp-textMuted text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(calculateQuoteTotal().subtotal)}</span>
                </div>
                {quoteDiscount > 0 && (
                  <div className="flex justify-between text-fp-textMuted text-sm">
                    <span>Discount ({quoteDiscount}%)</span>
                    <span>-{formatCurrency(calculateQuoteTotal().discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-fp-textMuted text-sm">
                  <span>GST ({quoteGST}%)</span>
                  <span>{formatCurrency(calculateQuoteTotal().gstAmount)}</span>
                </div>
                <div className="flex justify-between text-white text-lg font-bold pt-2 border-t border-white/10">
                  <span>Grand Total</span>
                  <span>{formatCurrency(calculateQuoteTotal().grandTotal)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setQuoteDialogOpen(false)} className="border-white/10">
                  Cancel
                </Button>
                <Button onClick={createQuote} className="btn-primary" data-testid="send-quote-btn">
                  <Send className="w-4 h-4 mr-2" />
                  Create & Send Quote
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default RequestsPage;
